import {
  RecurringOccurrenceStatusDto,
  RecurringReservationDayOfWeekDto,
  RecurringReservationErrorDetailsDtoCodeEnum,
  RecurringReservationStateDto,
  ReservationOverlapTypeDto,
} from '@/api/generated';

export type RecurringReservationsTranslation = {
  days: Record<RecurringReservationDayOfWeekDto, string>;
  daysShort: Record<RecurringReservationDayOfWeekDto, string>;
  details: {
    conflictCount: string;
    createdAt: string;
    customer: string;
    delete: string;
    deleteContent: string;
    deleteTitle: string;
    edit: string;
    ended: string;
    loadError: string;
    manager: string;
    note: string;
    occurrenceActions: {
      actions: string;
      cancel: string;
      cancelDialog: {
        back: string;
        confirm: string;
        content: string;
        error: string;
        title: string;
      };
      cancelLabel: string;
      move: string;
      moveDialog: {
        anonymousOfferItem: string;
        back: string;
        conflict: string;
        current: string;
        date: string;
        endOfDayHint: string;
        error: string;
        fullDayHint: string;
        hint: string;
        offerItem: string;
        recurringItem: string;
        submit: string;
        timeFrom: string;
        timeOrder: string;
        timeTo: string;
        title: string;
      };
      moveLabel: string;
      overlapTypes: Record<ReservationOverlapTypeDto, string>;
    };
    occurrenceCount: string;
    occurrences: {
      anonymousOffer: string;
      conflict: string;
      fullDay: string;
      heading: string;
      moved: string;
      offer: string;
      original: string;
      status: string;
      task: string;
      term: string;
    };
    period: string;
    resource: string;
    running: string;
    schedule: string;
  };
  errors: Record<RecurringReservationErrorDetailsDtoCodeEnum, string> & {
    overlap: string;
    overlapItem: string;
    unknown: string;
  };
  form: {
    cancel: string;
    customer: string;
    days: string;
    editTitle: string;
    endDate: string;
    endOfDayHint: string;
    fullDayHint: string;
    intervalWeeks: string;
    manager: string;
    name: string;
    note: string;
    resource: string;
    save: string;
    startDate: string;
    timeFrom: string;
    timeTo: string;
    title: string;
    validation: {
      dayRequired: string;
      endBeforeStart: string;
      interval: string;
      timeOrder: string;
      timesRequired: string;
      tooLong: string;
    };
  };
  list: {
    add: string;
    columns: {
      conflicts: string;
      customer: string;
      manager: string;
      name: string;
      period: string;
      resource: string;
      schedule: string;
    };
    filters: {
      customer: string;
      hasConflicts: string;
      manager: string;
      resource: string;
      search: string;
      state: string;
    };
    heading: string;
  };
  preview: {
    conflictCount: string;
    discardedChanges: string;
    futureOccurrenceCount: string;
    heading: string;
    incomplete: string;
    loadError: string;
    loading: string;
    noCollisions: string;
    occurrenceCount: string;
    offerCollisions: string;
    overlapCount: string;
    overlaps: string;
    phases: Record<ReservationOverlapTypeDto, string>;
    regeneration: string;
    scheduleUnchanged: string;
    taskCount: string;
  };
  schedule: {
    everyNWeeks: string;
    everyWeek: string;
    period: string;
  };
  state: Record<RecurringReservationStateDto, string>;
  status: Record<RecurringOccurrenceStatusDto, string>;
};

export const recurringReservationsTranslation: RecurringReservationsTranslation = {
  days: {
    FRIDAY: 'Piątek',
    MONDAY: 'Poniedziałek',
    SATURDAY: 'Sobota',
    SUNDAY: 'Niedziela',
    THURSDAY: 'Czwartek',
    TUESDAY: 'Wtorek',
    WEDNESDAY: 'Środa',
  },
  daysShort: {
    FRIDAY: 'pt',
    MONDAY: 'pn',
    SATURDAY: 'sb',
    SUNDAY: 'nd',
    THURSDAY: 'czw',
    TUESDAY: 'wt',
    WEDNESDAY: 'śr',
  },
  details: {
    conflictCount: 'Do przełożenia',
    createdAt: 'Utworzono',
    customer: 'Klient',
    delete: 'Usuń rezerwację stałą',
    deleteContent:
      'Przyszłe wystąpienia zostaną usunięte, a przeszłe pozostaną w historii. Tej operacji nie można cofnąć.',
    deleteTitle: 'Usunąć rezerwację stałą?',
    edit: 'Edytuj',
    ended: 'Zakończona',
    loadError: 'Nie udało się wczytać rezerwacji stałej.',
    manager: 'Zarządzający',
    note: 'Notatka',
    occurrenceActions: {
      actions: 'Akcje',
      cancel: 'Odwołaj',
      cancelDialog: {
        back: 'Wróć',
        confirm: 'Odwołaj wystąpienie',
        content:
          'Wystąpienie {{term}} zostanie odwołane i zwolni zasób, bo klient rezygnuje z tego terminu.',
        error: 'Nie udało się odwołać wystąpienia.',
        title: 'Odwołać wystąpienie?',
      },
      cancelLabel: 'Odwołaj wystąpienie {{term}}',
      move: 'Przesuń',
      moveDialog: {
        anonymousOfferItem: 'Potwierdzona rezerwacja oferty: {{term}} ({{type}})',
        back: 'Anuluj',
        conflict:
          'Nowy termin koliduje z potwierdzoną rezerwacją oferty albo inną rezerwacją stałą:',
        current: 'Obecny termin: {{term}}',
        date: 'Data',
        endOfDayHint: 'Koniec o 00:00 oznacza koniec dnia (24:00).',
        error: 'Nie udało się przesunąć wystąpienia.',
        fullDayHint:
          'Zasób jest rezerwowany na pełne dni, więc wystąpienie zajmie cały wybrany dzień.',
        hint: 'Pierwotny termin zostanie zapamiętany, a wystąpienie wróci do statusu „Aktywne”.',
        offerItem: 'Potwierdzona rezerwacja oferty {{name}}: {{term}} ({{type}})',
        recurringItem: 'Rezerwacja stała „{{name}}”: {{term}}',
        submit: 'Przesuń',
        timeFrom: 'Od',
        timeOrder: 'Koniec wystąpienia musi być późniejszy niż jego początek.',
        timeTo: 'Do',
        title: 'Przesuń wystąpienie',
      },
      moveLabel: 'Przesuń wystąpienie {{term}}',
      overlapTypes: {
        ASSEMBLY_EVENT: 'montaż',
        DISASSEMBLY_EVENT: 'demontaż',
        EVENT_ASSEMBLY: 'montaż',
        EVENT_DISASSEMBLY: 'demontaż',
        EVENT_EVENT: 'impreza',
      },
    },
    occurrenceCount: 'Wystąpienia',
    occurrences: {
      anonymousOffer: 'Potwierdzona oferta',
      conflict: 'Kolizja',
      fullDay: 'cały dzień',
      heading: 'Wystąpienia',
      moved: 'przesunięte',
      offer: 'Oferta {{name}}',
      original: 'Pierwotny termin',
      status: 'Status',
      task: 'Zadanie przełożenia',
      term: 'Termin',
    },
    period: 'Okres',
    resource: 'Zasób',
    running: 'Aktywna',
    schedule: 'Harmonogram',
  },
  errors: {
    overlap: 'Rezerwacja stała nakłada się na inną rezerwację stałą tego zasobu:',
    overlapItem: '{{term}} – „{{name}}” ({{conflictingTerm}})',
    RECURRING_RESERVATION_CALENDAR_INACTIVE:
      'Nie można założyć rezerwacji stałej na nieaktywnym zasobie.',
    RECURRING_RESERVATION_CALENDAR_NOT_FOUND: 'Wybrany zasób nie ma kalendarza.',
    RECURRING_RESERVATION_CUSTOMER_NOT_FOUND: 'Wybrany klient nie istnieje.',
    RECURRING_RESERVATION_CUSTOMER_REJECTED:
      'Odrzucony klient nie może być klientem rezerwacji stałej.',
    RECURRING_RESERVATION_CUSTOMER_REQUIRED: 'Wskaż klienta rezerwacji stałej.',
    RECURRING_RESERVATION_DATES_REQUIRED: 'Podaj datę startu i datę końca.',
    RECURRING_RESERVATION_DAY_DUPLICATED:
      'Każdy dzień tygodnia może mieć tylko jeden przedział godzin.',
    RECURRING_RESERVATION_DAYS_REQUIRED: 'Wybierz co najmniej jeden dzień tygodnia.',
    RECURRING_RESERVATION_END_BEFORE_START: 'Data końca nie może być wcześniejsza niż data startu.',
    RECURRING_RESERVATION_INTERVAL_OUT_OF_RANGE: 'Interwał musi wynosić od 1 do 52 tygodni.',
    RECURRING_RESERVATION_MANAGER_INACTIVE:
      'Zarządzającym rezerwacją stałą może być tylko aktywny użytkownik.',
    RECURRING_RESERVATION_MANAGER_REQUIRED: 'Wskaż zarządzającego rezerwacją stałą.',
    RECURRING_RESERVATION_NAME_REQUIRED: 'Podaj nazwę rezerwacji stałej.',
    RECURRING_RESERVATION_NAME_TOO_LONG: 'Nazwa rezerwacji stałej jest za długa.',
    RECURRING_RESERVATION_NO_OCCURRENCES:
      'Harmonogram nie daje żadnego wystąpienia między datą startu i końca.',
    RECURRING_RESERVATION_NOT_FOUND: 'Rezerwacja stała nie istnieje.',
    RECURRING_RESERVATION_NOT_MANAGED:
      'Bez uprawnienia do zarządzania wszystkimi rezerwacjami stałymi można zmieniać tylko własne.',
    RECURRING_RESERVATION_NOTE_TOO_LONG: 'Notatka rezerwacji stałej jest za długa.',
    RECURRING_RESERVATION_OCCURRENCE_NOT_FOUND: 'Wystąpienie rezerwacji stałej nie istnieje.',
    RECURRING_RESERVATION_OCCURRENCE_RANGE_INVALID:
      'Koniec wystąpienia musi być późniejszy niż jego początek.',
    RECURRING_RESERVATION_TIME_INVALID: 'Godzina musi mieć format GG:MM.',
    RECURRING_RESERVATION_TIME_RANGE_CROSSES_MIDNIGHT:
      'Przedział godzin nie może przechodzić przez północ.',
    RECURRING_RESERVATION_TIME_RANGE_EMPTY:
      'Godzina końca musi być późniejsza niż godzina początku.',
    RECURRING_RESERVATION_TIMES_NOT_ALLOWED:
      'Zasób jest rezerwowany na pełne dni, więc rezerwacja stała nie ma godzin.',
    RECURRING_RESERVATION_TIMES_REQUIRED: 'Podaj godziny dla każdego wybranego dnia.',
    RECURRING_RESERVATION_TOO_LONG: 'Rezerwacja stała może trwać najwyżej 2 lata.',
    unknown: 'Nie udało się zapisać rezerwacji stałej.',
  },
  form: {
    cancel: 'Anuluj',
    customer: 'Klient',
    days: 'Dni tygodnia',
    editTitle: 'Edycja rezerwacji stałej',
    endDate: 'Data końca',
    endOfDayHint: 'Koniec o 00:00 oznacza koniec dnia (24:00).',
    fullDayHint: 'Zasób jest rezerwowany na pełne dni, więc wybierasz tylko dni tygodnia.',
    intervalWeeks: 'Co ile tygodni',
    manager: 'Zarządzający',
    name: 'Nazwa',
    note: 'Notatka',
    resource: 'Zasób',
    save: 'Zapisz',
    startDate: 'Data startu',
    timeFrom: 'Od',
    timeTo: 'Do',
    title: 'Nowa rezerwacja stała',
    validation: {
      dayRequired: 'Wybierz co najmniej jeden dzień tygodnia.',
      endBeforeStart: 'Data końca nie może być wcześniejsza niż data startu.',
      interval: 'Podaj liczbę od 1 do 52.',
      timeOrder:
        'Godzina końca musi być późniejsza niż godzina początku (bez przechodzenia przez północ).',
      timesRequired: 'Podaj godziny dla każdego wybranego dnia.',
      tooLong: 'Rezerwacja stała może trwać najwyżej 2 lata.',
    },
  },
  list: {
    add: 'Dodaj rezerwację stałą',
    columns: {
      conflicts: 'Do przełożenia',
      customer: 'Klient',
      manager: 'Zarządzający',
      name: 'Nazwa',
      period: 'Okres',
      resource: 'Zasób',
      schedule: 'Harmonogram',
    },
    filters: {
      customer: 'Klient',
      hasConflicts: 'Ma konflikty',
      manager: 'Zarządzający',
      resource: 'Zasób',
      search: 'Szukaj po nazwie',
      state: 'Stan',
    },
    heading: 'Rezerwacje stałe',
  },
  preview: {
    conflictCount: 'Do przełożenia',
    discardedChanges:
      'Ręcznie przesunięte lub odwołane przyszłe wystąpienia, których zmiany przepadną: {{count}}.',
    futureOccurrenceCount: 'Wystąpienia od jutra',
    heading: 'Podgląd skutków',
    incomplete: 'Wybierz zasób, dni tygodnia z godzinami, interwał i daty, aby zobaczyć podgląd.',
    loadError: 'Nie udało się przygotować podglądu.',
    loading: 'Aktualizuję podgląd…',
    noCollisions:
      'Brak kolizji z innymi rezerwacjami stałymi i z potwierdzonymi rezerwacjami ofert.',
    occurrenceCount: 'Wystąpienia',
    offerCollisions:
      'Kolizje z potwierdzonymi rezerwacjami ofert: oferty mają pierwszeństwo, więc te wystąpienia zostaną zapisane jako „Do przełożenia”, a zarządzający dostanie zadanie przełożenia dla każdej oferty:',
    overlapCount: 'Kolizje z seriami',
    overlaps: 'Nakłada się na inną rezerwację stałą tego zasobu, więc zapis zostanie odrzucony:',
    phases: {
      ASSEMBLY_EVENT: 'montaż',
      DISASSEMBLY_EVENT: 'demontaż',
      EVENT_ASSEMBLY: 'impreza',
      EVENT_DISASSEMBLY: 'impreza',
      EVENT_EVENT: 'impreza',
    },
    regeneration:
      'Zmiana harmonogramu lub dat wygeneruje od nowa wystąpienia od jutra; dzisiejsze i wcześniejsze zostaną bez zmian. Ręczne przesunięcia i odwołania przyszłych wystąpień przepadną.',
    scheduleUnchanged: 'Harmonogram i daty bez zmian: wystąpienia zostaną takie, jak są.',
    taskCount: 'Zadania przełożenia',
  },
  schedule: {
    everyNWeeks: 'co {{count}} tyg.',
    everyWeek: 'co tydzień',
    period: '{{from}} – {{to}}',
  },
  state: {
    ACTIVE: 'Aktywne',
    ENDED: 'Zakończone',
  },
  status: {
    ACTIVE: 'Aktywne',
    CANCELLED: 'Odwołane',
    CONFLICT: 'Do przełożenia',
  },
};
